import { X } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react'

import { cn } from './utils'

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  /** Selected/pressed state (filter chips, per-feature accept chips). */
  selected?: boolean
  /** Show a trailing × that calls `onRemove` without triggering `onClick`. */
  onRemove?: () => void
  icon?: ReactNode
  /** Sets `--kind` so the chip can carry a vocabulary colour. */
  kindColor?: string
  size?: 'sm' | 'md'
}

/**
 * Interactive pill: filter facets, proposal accept/reject chips, kind
 * pickers. Minimum height 36 px (`md`) so it is tappable; `sm` for dense
 * rails where the mouse is the only input.
 */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { className, selected = false, onRemove, icon, kindColor, size = 'md', children, style, type = 'button', ...props },
  ref,
) {
  const handleRemove = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    onRemove?.()
  }
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={selected}
      style={kindColor ? { ...style, ['--kind' as string]: kindColor } : style}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border text-sm font-medium transition-colors select-none',
        'focus-visible:focus-ring disabled:pointer-events-none disabled:opacity-50',
        size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3',
        selected ? 'border-transparent bg-accent-soft text-ink' : 'border-line bg-paper text-ink-2 hover:bg-paper-2 hover:text-ink',
        kindColor && 'pl-2',
        className,
      )}
      {...props}
    >
      {kindColor ? <span className="size-2.5 rounded-full kind-bg" aria-hidden /> : null}
      {icon}
      <span className="truncate">{children}</span>
      {onRemove ? (
        <span
          role="button"
          tabIndex={-1}
          aria-label="Remove"
          onClick={handleRemove}
          className="-mr-1 inline-flex size-5 items-center justify-center rounded-full hover:bg-paper-3"
        >
          <X className="size-3.5" aria-hidden />
        </span>
      ) : null}
    </button>
  )
})
